/**
 * Build 5: the sample's shared registers, row by row, refusing what the
 * platform refuses (backend registers/rows.py).
 *
 * Each register starts from the sample, cut into rows by its spec, every
 * time the page loads — so the sample's dates stay where they are against
 * the clock. What this browser changes is kept on top of that, row by row,
 * in this browser's storage: a reload keeps the person's edits, and only the
 * rows they touched stop moving with the clock.
 *
 * The conflict rule is the platform's: a change names the version it was
 * made from, and a stale one is refused with who changed the row and when.
 */
import { DataSourceError } from "../api/dataSource";
import {
  REGISTER_KEYS,
  RowConflict,
  noteRows,
  plain,
  sameRow,
  type RegisterKey,
  type RegisterRow,
  type RegisterRows,
  type RowOp,
} from "../domain/registerRows";
import { CONTRACTS_SPEC, EQUIPMENT_SPEC, FACILITY_SPEC, VDGS_SPEC } from "../domain/registerSpecs";
import { mockContracts } from "./contracts";
import { mockFleet } from "./fleet";
import { sampleInspectionStore } from "./assetInspections";
import { mockVdgs } from "./vdgs";

type Seed = () => { id: string; data: Record<string, unknown> }[];

export const SAMPLE_SEEDS: Partial<Record<RegisterKey, Seed>> = {
  vdgs: () => noteRows(VDGS_SPEC, mockVdgs()),
  equipment: () => noteRows(EQUIPMENT_SPEC, mockFleet()),
  "facility-inspections": () => noteRows(FACILITY_SPEC, sampleInspectionStore()),
  contracts: () => noteRows(CONTRACTS_SPEC, mockContracts()),
};

const storeKey = (siteId: string, key: RegisterKey) => `slt.rows.${siteId}.${key}`;

function readKept(siteId: string, key: RegisterKey): Record<string, RegisterRow> {
  try {
    const raw = localStorage.getItem(storeKey(siteId, key));
    return raw ? (JSON.parse(raw) as Record<string, RegisterRow>) : {};
  } catch {
    return {};
  }
}

function writeKept(siteId: string, key: RegisterKey, rows: Record<string, RegisterRow>): void {
  try {
    localStorage.setItem(storeKey(siteId, key), JSON.stringify(rows));
  } catch {
    /* storage off: the change lasts as long as the page */
  }
}

export class MockRegisters {
  private readonly loaded = new Map<string, { rows: RegisterRow[]; kept: Record<string, RegisterRow> }>();

  private readonly who: () => string;
  private readonly seeds: Partial<Record<RegisterKey, Seed>>;

  constructor(who: () => string, seeds: Partial<Record<RegisterKey, Seed>> = SAMPLE_SEEDS) {
    this.who = who;
    this.seeds = seeds;
  }

  private check(key: string): RegisterKey {
    if (!(REGISTER_KEYS as readonly string[]).includes(key))
      throw new DataSourceError(`Not found: there is no register called '${key}'`, { status: 404 });
    return key as RegisterKey;
  }

  private state(siteId: string, key: RegisterKey) {
    const k = `${siteId}|${key}`;
    let s = this.loaded.get(k);
    if (!s) {
      const at = new Date().toISOString();
      const seeded: RegisterRow[] = (this.seeds[key]?.() ?? []).map((r) => ({
        id: r.id,
        data: r.data,
        version: 1,
        createdAt: at,
        createdBy: "Sample",
        updatedAt: at,
        updatedBy: "Sample",
      }));
      const kept = readKept(siteId, key);
      const rows = seeded.map((r) => kept[r.id] ?? r);
      for (const [id, r] of Object.entries(kept)) if (!seeded.some((x) => x.id === id)) rows.push(r);
      s = { rows, kept };
      this.loaded.set(k, s);
    }
    return s;
  }

  list(siteId: string, key: string, includeDeleted = false): RegisterRows {
    const reg = this.check(key);
    const s = this.state(siteId, reg);
    return {
      register: reg,
      rows: structuredClone(s.rows.filter((r) => includeDeleted || !r.deletedAt)),
      ever: s.rows.length,
      canWrite: true,
    };
  }

  /** every change or none, as the platform's batch */
  change(siteId: string, key: string, ops: RowOp[]): RegisterRow[] {
    const reg = this.check(key);
    if (!ops.length) throw new DataSourceError("send at least one change", { status: 400 });
    const s = this.state(siteId, reg);
    const work = new Map(s.rows.map((r) => [r.id, r]));
    const order = s.rows.map((r) => r.id);
    const who = this.who();
    const at = new Date().toISOString();
    const out: RegisterRow[] = [];
    const seen = new Set<string>();
    for (const op of ops) {
      if (seen.has(op.id)) throw new DataSourceError(`row '${op.id}' is changed twice in one save`, { status: 400 });
      seen.add(op.id);
      const cur = work.get(op.id);
      if (op.op === "put") {
        if (op.version == null) {
          if (cur)
            throw conflictOf(cur.deletedAt ? "row_deleted" : "row_exists", reg, cur);
          const row: RegisterRow = { id: op.id, data: plain(op.data), version: 1, createdAt: at, createdBy: who, updatedAt: at, updatedBy: who };
          work.set(op.id, row);
          order.push(op.id);
          out.push(row);
          continue;
        }
        if (!cur) throw new DataSourceError("Not found: this row is not kept here", { status: 404 });
        if (cur.version !== op.version) throw conflictOf(cur.deletedAt ? "row_deleted" : "row_changed", reg, cur);
        if (sameRow(cur.data, op.data) && !cur.deletedAt) {
          out.push(cur);
          continue;
        }
        const row: RegisterRow = {
          ...cur,
          data: plain(op.data),
          version: cur.version + 1,
          updatedAt: at,
          updatedBy: who,
          deletedAt: undefined,
          deletedBy: undefined,
        };
        work.set(op.id, row);
        out.push(row);
      } else {
        if (!cur) throw new DataSourceError("Not found: this row is not kept here", { status: 404 });
        if (cur.deletedAt) {
          out.push(cur);
          continue;
        }
        if (cur.version !== op.version) throw conflictOf("row_changed", reg, cur);
        const row: RegisterRow = { ...cur, version: cur.version + 1, deletedAt: at, deletedBy: who, updatedAt: at, updatedBy: who };
        work.set(op.id, row);
        out.push(row);
      }
    }
    /* nothing above threw: the batch is taken whole */
    s.rows = order.map((id) => work.get(id)!);
    for (const r of out) s.kept[r.id] = r;
    writeKept(siteId, reg, s.kept);
    return structuredClone(out);
  }

  import(siteId: string, key: string, rows: { id: string; data: Record<string, unknown> }[]): { imported: number } {
    const reg = this.check(key);
    const s = this.state(siteId, reg);
    if (s.rows.length)
      throw new DataSourceError("the register already holds rows; nothing was brought across", {
        status: 409,
        detail: { error: "register_not_empty" },
      });
    if (!rows.length) throw new DataSourceError("there is nothing to bring across", { status: 400 });
    const who = this.who();
    const at = new Date().toISOString();
    for (const r of rows) {
      const row: RegisterRow = { id: r.id, data: plain(r.data), version: 1, createdAt: at, createdBy: who, updatedAt: at, updatedBy: who };
      s.rows.push(row);
      s.kept[r.id] = row;
    }
    writeKept(siteId, reg, s.kept);
    return { imported: rows.length };
  }
}

function conflictOf(kind: "row_changed" | "row_exists" | "row_deleted", key: RegisterKey, cur: RegisterRow): RowConflict {
  const who = (kind === "row_deleted" ? cur.deletedBy : cur.updatedBy) ?? "someone";
  const when = (kind === "row_deleted" ? cur.deletedAt : cur.updatedAt) ?? "";
  const msg =
    kind === "row_deleted"
      ? `${who} deleted this row at ${when}.`
      : kind === "row_exists"
        ? `A row with this id is already kept, last changed by ${who} at ${when}.`
        : `${who} changed this row at ${when}, after you read it.`;
  return new RowConflict(msg, { kind, register: key, rowId: cur.id, current: structuredClone(cur) });
}
